'use client';
import { motion } from 'framer-motion';
import { useEffect, useRef, useCallback, useMemo } from 'react';

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  baseX: number;
  baseY: number;
  pulsePhase: number;
  textIndex: number;
}

interface Dot {
  id: number;
  delay: number;
  duration: number;
  size: number;
  x: string;
  y: string;
}

export const TextConstellation = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const nodesRef = useRef<Node[]>([]);
  const mousePositionRef = useRef({ x: 0, y: 0, active: false });

  // Configuration - memoized to prevent unnecessary re-renders
  const config = useMemo(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    return {
      nodeCount: isMobile ? 50 : 200,
      maxDistance: isMobile ? 35 : 45,
      mouseForce: 0.08,
      centerForce: 0.003,
    };
  }, []);

  // Text path generation with responsive layout
  const generateTextPath = (width: number, height: number) => {
    const points: { x: number; y: number }[] = [];
    const isMobile = width < 768;

    // Responsive font sizing
    const fontSize = isMobile
      ? Math.min(width, height) * 0.12
      : Math.min(width, height) * 0.15;
    const letterSpacing = fontSize * 1.1;

    // Split text for mobile layout
    const texts = isMobile ? ["Ateeb", "NoOne"] : ["AteebNoOne"];
    const lineHeight = fontSize * 1.4; // Space between lines

    texts.forEach((text, lineIndex) => {
      // More accurate text width calculation - account for letter spacing and character width
      const actualTextWidth = (text.length - 1) * letterSpacing + fontSize * 0.8;
      const startX = (width - actualTextWidth) / 2;

      // Center vertically with better precision
      const baseY = height * 0.35;
      const centerY = isMobile
        ? baseY - (lineHeight / 2) + (lineIndex * lineHeight)  // Position higher on mobile
        : baseY;  // Centered for desktop

      // Define letter shapes as point arrays (relative coordinates)
      const letterShapes = {
        // Uppercase letters
        A: [
          // Left side
          [0.1, 0.5], [0.1, 0.3], [0.2, 0.1], [0.3, -0.1], [0.4, -0.3], [0.5, -0.5],
          // Right side
          [0.5, -0.5], [0.6, -0.3], [0.7, -0.1], [0.8, 0.1], [0.9, 0.3], [0.9, 0.5],
          // Crossbar
          [0.25, 0.1], [0.35, 0.1], [0.45, 0.1], [0.55, 0.1], [0.65, 0.1], [0.75, 0.1]
        ],
        N: [
          // Left vertical
          [0.1, -0.5], [0.1, -0.3], [0.1, -0.1], [0.1, 0.1], [0.1, 0.3], [0.1, 0.5],
          // Right vertical  
          [0.9, -0.5], [0.9, -0.3], [0.9, -0.1], [0.9, 0.1], [0.9, 0.3], [0.9, 0.5],
          // Diagonal
          [0.1, -0.5], [0.2, -0.3], [0.3, -0.1], [0.4, 0.1], [0.5, 0.3], [0.6, 0.5],
          [0.7, 0.3], [0.8, 0.1], [0.9, -0.1]
        ],
        O: [
          // Top arc
          [0.3, -0.5], [0.4, -0.5], [0.5, -0.5], [0.6, -0.5], [0.7, -0.5],
          // Right arc
          [0.8, -0.4], [0.9, -0.2], [0.9, 0], [0.9, 0.2], [0.8, 0.4],
          // Bottom arc
          [0.7, 0.5], [0.6, 0.5], [0.5, 0.5], [0.4, 0.5], [0.3, 0.5],
          // Left arc
          [0.2, 0.4], [0.1, 0.2], [0.1, 0], [0.1, -0.2], [0.2, -0.4]
        ],

        // Lowercase letters
        t: [
          // Vertical stem
          [0.4, -0.4], [0.4, -0.2], [0.4, 0], [0.4, 0.2], [0.4, 0.4], [0.4, 0.5],
          // Horizontal crossbar
          [0.1, -0.2], [0.2, -0.2], [0.3, -0.2], [0.5, -0.2], [0.6, -0.2], [0.7, -0.2],
          // Curved bottom
          [0.4, 0.5], [0.5, 0.5], [0.6, 0.4]
        ],
        e: [
          // Top horizontal
          [0.1, -0.2], [0.3, -0.2], [0.5, -0.2], [0.7, -0.2], [0.8, -0.1],
          // Right curve
          [0.8, 0], [0.7, 0.1], [0.6, 0.1], [0.5, 0.1], [0.4, 0.1],
          // Middle horizontal
          [0.1, 0.1], [0.2, 0.1], [0.3, 0.1],
          // Left vertical
          [0.1, -0.1], [0.1, 0], [0.1, 0.2], [0.1, 0.3],
          // Bottom curve
          [0.2, 0.4], [0.3, 0.5], [0.5, 0.5], [0.7, 0.5], [0.8, 0.4]
        ],
        b: [
          // Left vertical stem
          [0.1, -0.5], [0.1, -0.3], [0.1, -0.1], [0.1, 0.1], [0.1, 0.3], [0.1, 0.5],
          // Top curve
          [0.1, -0.1], [0.3, -0.2], [0.5, -0.2], [0.7, -0.1], [0.7, 0],
          // Middle connection
          [0.7, 0], [0.6, 0.1], [0.4, 0.1], [0.2, 0.1], [0.1, 0.1],
          // Bottom curve
          [0.1, 0.1], [0.3, 0.2], [0.5, 0.2], [0.7, 0.3], [0.7, 0.4], [0.6, 0.5], [0.4, 0.5], [0.2, 0.4]
        ],
        o: [
          // Top arc
          [0.3, -0.2], [0.4, -0.2], [0.5, -0.2], [0.6, -0.2], [0.7, -0.1],
          // Right arc
          [0.8, 0], [0.8, 0.1], [0.8, 0.2], [0.7, 0.3], [0.6, 0.4],
          // Bottom arc
          [0.5, 0.5], [0.4, 0.5], [0.3, 0.5], [0.2, 0.4], [0.1, 0.3],
          // Left arc
          [0.1, 0.2], [0.1, 0.1], [0.1, 0], [0.2, -0.1], [0.3, -0.2]
        ],
        n: [
          // Left vertical
          [0.1, -0.2], [0.1, 0], [0.1, 0.2], [0.1, 0.4], [0.1, 0.5],
          // Top curve
          [0.1, -0.2], [0.2, -0.2], [0.4, -0.2], [0.6, -0.1], [0.7, 0],
          // Right vertical
          [0.7, 0], [0.7, 0.2], [0.7, 0.4], [0.7, 0.5]
        ],

        // Default shape for unknown characters
        default: [
          [0.1, -0.5], [0.1, 0.5], [0.9, 0.5], [0.9, -0.5], [0.1, -0.5]
        ]
      };

      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        // Use the specific shape or default
        const shape = letterShapes[char as keyof typeof letterShapes] || letterShapes.default;

        const letterX = startX + i * letterSpacing;

        // Add points for this letter with fine-tuned centering
        shape.forEach(([relX, relY]) => {
          const x = letterX + relX * fontSize;
          const y = centerY + relY * fontSize;

          points.push({
            x: x + (Math.random() - 0.5) * 2, // Further reduced noise for clearer text
            y: y + (Math.random() - 0.5) * 2
          });
        });
      }
    });

    return points;
  };

  // Handle mouse movement for interaction
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    mousePositionRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      active: true,
    };
  }, []);

  const handleMouseLeave = useCallback(() => {
    mousePositionRef.current.active = false;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with high DPI support
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = window.innerWidth;
      const height = window.innerHeight;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      // Reinitialize nodes if canvas size changes significantly
      if (Math.abs(width - (parseInt(canvas.dataset.lastWidth || '0'))) > 100 ||
        Math.abs(height - (parseInt(canvas.dataset.lastHeight || '0'))) > 100) {
        initNodes(width, height);
        canvas.dataset.lastWidth = width.toString();
        canvas.dataset.lastHeight = height.toString();
      }
    };

    // Initialize nodes along text path
    const initNodes = (width: number, height: number) => {
      const textPoints = generateTextPath(width, height);
      const nodes: Node[] = [];

      // Make sure we have enough points
      if (textPoints.length === 0) {
        // Fallback if text generation fails
        for (let i = 0; i < config.nodeCount; i++) {
          nodes.push({
            x: width / 2 + (Math.random() - 0.5) * 100,
            y: height / 2 + (Math.random() - 0.5) * 100,
            vx: (Math.random() - 0.5) * 0.2,
            vy: (Math.random() - 0.5) * 0.2,
            radius: 1.5 + Math.random() * 1,
            opacity: 0.6 + Math.random() * 0.3,
            baseX: width / 2,
            baseY: height / 2,
            pulsePhase: Math.random() * Math.PI * 2,
            textIndex: 0,
          });
        }
      } else {
        for (let i = 0; i < config.nodeCount; i++) {
          const pointIndex = Math.floor((i / config.nodeCount) * textPoints.length);
          const point = textPoints[pointIndex];

          nodes.push({
            x: point.x,
            y: point.y,
            vx: (Math.random() - 0.5) * 0.2,
            vy: (Math.random() - 0.5) * 0.2,
            radius: 1.5 + Math.random() * 1,
            opacity: 0.6 + Math.random() * 0.3,
            baseX: point.x,
            baseY: point.y,
            pulsePhase: Math.random() * Math.PI * 2,
            textIndex: pointIndex,
          });
        }
      }

      nodesRef.current = nodes;
    };

    resizeCanvas();

    // Add event listeners
    window.addEventListener('resize', resizeCanvas);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    const animate = () => {
      if (!ctx) return;

      const width = parseInt(canvas.style.width);
      const height = parseInt(canvas.style.height);

      // Clear canvas with a subtle fade effect for trails - black background
      ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.fillRect(0, 0, width, height);

      const nodes = nodesRef.current;
      const mouse = mousePositionRef.current;

      // Update node positions
      nodes.forEach(node => {
        // Add pulsing effect
        node.pulsePhase += 0.015;

        // Stronger attraction to text path
        const dx = node.baseX - node.x;
        const dy = node.baseY - node.y;
        node.vx += dx * config.centerForce;
        node.vy += dy * config.centerForce;

        // Mouse interaction - repulsion effect
        if (mouse.active) {
          const mouseDx = node.x - mouse.x;
          const mouseDy = node.y - mouse.y;
          const mouseDistance = Math.sqrt(mouseDx * mouseDx + mouseDy * mouseDy);

          if (mouseDistance < 120) {
            const force = (1 - mouseDistance / 120) * config.mouseForce;
            node.vx += (mouseDx / mouseDistance) * force;
            node.vy += (mouseDy / mouseDistance) * force;
          }
        }

        // Apply velocity
        node.x += node.vx;
        node.y += node.vy;

        // Apply friction
        node.vx *= 0.95;
        node.vy *= 0.95;
      });

      // Draw connections between nearby nodes in text
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const nodeA = nodes[i];
          const nodeB = nodes[j];

          const dx = nodeA.x - nodeB.x;
          const dy = nodeA.y - nodeB.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Only connect nodes that are close and part of similar text regions
          const textIndexDiff = Math.abs(nodeA.textIndex - nodeB.textIndex);
          if (distance < config.maxDistance && textIndexDiff < 20) {
            const opacity = Math.pow(1 - distance / config.maxDistance, 2) * 0.4;

            // White connections
            ctx.strokeStyle = `rgba(255, 255, 255, ${opacity * 0.6})`;
            ctx.lineWidth = 0.6;

            ctx.beginPath();
            ctx.moveTo(nodeA.x, nodeA.y);
            ctx.lineTo(nodeB.x, nodeB.y);
            ctx.stroke();
          }
        }
      }

      // Draw nodes with white glow
      nodes.forEach(node => {
        // Outer glow - white
        const outerGradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius * 4
        );

        outerGradient.addColorStop(0, `rgba(255, 255, 255, ${node.opacity * 0.8})`);
        outerGradient.addColorStop(0.5, `rgba(255, 255, 255, ${node.opacity * 0.4})`);
        outerGradient.addColorStop(1, `rgba(255, 255, 255, 0)`);

        ctx.fillStyle = outerGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 4, 0, Math.PI * 2);
        ctx.fill();

        // Inner glow - white
        const innerGradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius * 2
        );

        innerGradient.addColorStop(0, `rgba(255, 255, 255, ${node.opacity})`);
        innerGradient.addColorStop(1, `rgba(255, 255, 255, ${node.opacity * 0.6})`);

        ctx.fillStyle = innerGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * 2, 0, Math.PI * 2);
        ctx.fill();

        // Core of the node - bright white
        ctx.fillStyle = `rgba(255, 255, 255, ${Math.min(node.opacity + 0.3, 1)})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (canvas) {
        canvas.removeEventListener('mousemove', handleMouseMove);
        canvas.removeEventListener('mouseleave', handleMouseLeave);
      }
      cancelAnimationFrame(animationRef.current);
    };
  }, [config, handleMouseMove, handleMouseLeave]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-auto cursor-pointer z-10"
      style={{ background: 'transparent' }}
    />
  );
};

export const FloatingConstellation = () => {
  // Create deterministic but visually pleasing dots
  const dots: Dot[] = Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * Math.PI * 2;
    const radius = 35 + (i % 4) * 20;

    return {
      id: i,
      delay: (Math.sin(i * 1.3) + 1) * 1.5,
      duration: 5 + ((Math.cos(i * 0.7) + 1) * 3),
      size: 1 + (Math.sin(i) * 0.5),
      x: `${50 + Math.cos(angle) * radius}%`,
      y: `${50 + Math.sin(angle) * radius}%`,
    };
  });

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {dots.map((dot) => (
        <motion.div
          key={dot.id}
          className="absolute w-2 h-2 bg-white/90 rounded-full shadow-lg shadow-white/50"
          initial={{
            scale: 0,
            opacity: 0,
            x: dot.x,
            y: dot.y,
          }}
          animate={{
            scale: [1, 1.6, 1],
            opacity: [0.4, 0.9, 0.4],
            x: [
              dot.x,
              `${50 + (Math.sin(dot.id * 0.7) * 25)}%`,
              dot.x
            ],
            y: [
              dot.y,
              `${50 + (Math.cos(dot.id * 0.9) * 25)}%`,
              dot.y
            ],
          }}
          transition={{
            duration: dot.duration,
            repeat: Infinity,
            delay: dot.delay,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};
